import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  CircularProgress,
  LinearProgress,
  Typography,
  IconButton
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import {
  FormField2,
  getValidationSchema,
  IDynamicFormValue,
  ISelectOption
} from 'src/lib';
import * as Yup from 'yup';
import { DynamicFormFieldRenderer } from 'src/components/analytics';
import {
  createFieldToOptionsMap,
  getAutoFillValue,
  getNextValues,
  transformDynamicFormValueToFilterPayload
} from 'src/components/filters/utils/filter-form-utils';
import MODULE_TO_PAGE_MAP from '../../../home/types/simulation-module-to-page-map';
import { createSKU } from '../../api/planner-api';
import { getAddSKUFormFields } from '../../store/planner-slice-async-thunks';
import {
  setSkuFormFieldOptions,
  toggleDisableStateForSkuFormFields
} from '../../store/planner-slice';

function StyledLabel({ label }: { label: string }) {
  return (
    <Typography
      component='label'
      fontSize={14}
      fontWeight={400}
      textAlign='right'
    >
      {label}
    </Typography>
  );
}

interface IAddSKUDialogProps {
  /**
   * Boolean indicating the state of dialog visibility
   */
  isOpen: boolean;
  /**
   * Close handler for dialog
   * @param event Click event details
   * @param reason Reason for close
   * @param isSummaryRefreshRequired Is grid and summary table refresh required
   * @returns void
   */
  onClose: (
    event: unknown,
    reason: 'backdropClick' | 'escapeKeyDown',
    isSummaryRefreshRequired?: boolean
  ) => void;
}

export default function AddSKUDialog({ isOpen, onClose }: IAddSKUDialogProps) {
  const appDispatch = useAppDispatch();
  const [isAddingSKU, setIsAddingSKU] = useState(false);
  const { moduleId, scenarioId, sourceId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.PLANNER
  });

  const { moduleId: innovationModuleId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.INNOVATION
  });
  const [validationSchema, setValidationSchema] = useState(
    Yup.object().shape({})
  );

  const [initialValues, setInitialValues] = useState({});
  const { formFields, isFetchingFormFields, addSKUFieldCombinations } =
    useAppSelector((state) => state.plannerV2);

  const {
    globalFilterValue,
    globalFilterCombinations,
    globalFilterFormFields
  } = useAppSelector((state) => state.filters);

  const skuForm = useFormik<IDynamicFormValue>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log(values);
    }
  });

  /**
   * Returns filtered fields for which validation is required
   * @param fields All fields in the form
   * @param ppgToggle The current value of toggle (new or existing ppg)
   * @returns Fields for which validation schema is required
   */
  const getFieldsForValidationSchema = (
    fields: FormField2[],
    ppgToggle: boolean
  ) =>
    fields.filter((field) => {
      if (field.name === 'ppg') {
        return ppgToggle
          ? field.control_type === 'TEXT'
          : field.control_type === 'SINGLESELECT-DROPDOWN';
      }
      return field.planner_flag;
    });

  const resetDropdownOptionsOfAllFields = (toggle: boolean) => {
    const defaultOptionsToFieldMap = createFieldToOptionsMap(
      getFieldsForValidationSchema(formFields, toggle),
      addSKUFieldCombinations
    );
    appDispatch(setSkuFormFieldOptions(defaultOptionsToFieldMap));
  };

  /**
   * A function to get validation schema for formik
   * @param fields All fields
   * @param ppgToggle The current value of toggle (new or existing ppg)
   * @returns a Yup schema validation object for each of the field
   */
  const getValidationSchemaForFields = (fields: FormField2[]) =>
    fields
      .map((field) => getValidationSchema(field))
      .reduce(
        (prev, curr) => ({ ...prev, ...curr }),
        {} as { [key: string]: any }
      );

  const handleChange = (newValue: unknown, field: FormField2) => {
    if (field.name === 'ppgToggle') {
      skuForm.setFieldValue('ppg', newValue ? '' : [], true);

      setTimeout(() => {
        skuForm.validateForm();
      }, 300);

      // enabling/disabling sku fields
      appDispatch(
        toggleDisableStateForSkuFormFields({
          shouldEnableAutoFillFields: newValue as boolean
        })
      );

      const schema = getValidationSchemaForFields(
        getFieldsForValidationSchema(formFields, newValue as boolean)
      );
      setValidationSchema(Yup.object().shape(schema));
      if (newValue) {
        // Resetting all the dropdown options when ppg is set to new ppg toggle
        resetDropdownOptionsOfAllFields(newValue as boolean);
      }
      skuForm.setFieldValue(field.name, newValue, true);
    } else if (
      field.name === 'ppg' &&
      field.control_type === 'SINGLESELECT-DROPDOWN'
    ) {
      const fieldsToAutoFill = formFields.filter(
        (formField) => formField.is_auto_fill
      );
      const autoFilledValuesMap = getAutoFillValue(
        addSKUFieldCombinations,
        'ppg',
        (newValue as ISelectOption[])[0].value,
        fieldsToAutoFill.map((formField) => formField.name)
      );
      const newValues = fieldsToAutoFill.reduce((prev, curr) => {
        if (
          curr.control_type === 'MULTISELECT-DROPDOWN' ||
          curr.control_type === 'SINGLESELECT-DROPDOWN'
        ) {
          return {
            ...prev,
            [curr.name]:
              curr.control_type === 'MULTISELECT-DROPDOWN'
                ? (
                    autoFilledValuesMap[curr.name] as string[]
                  ).map<ISelectOption>((value) => ({ label: value, value }))
                : (autoFilledValuesMap[curr.name] as string[])
                    .map<ISelectOption>((value) => ({ label: value, value }))
                    .slice(0, 1)
          };
        }
        return {
          ...prev,
          [curr.name]: (autoFilledValuesMap[curr.name] as any[])[0]
        };
      }, {});
      skuForm.setValues({
        ...skuForm.values,
        ...newValues,
        [field.name]: newValue
      });
    } else if (field.is_hierarchical && field.name !== 'ppg') {
      const { fieldNameToOptionsMap, newFormValues } = getNextValues(
        field.name,
        newValue as ISelectOption[],
        getFieldsForValidationSchema(formFields, skuForm.values.ppgToggle),
        addSKUFieldCombinations,
        skuForm.values
      );
      appDispatch(setSkuFormFieldOptions(fieldNameToOptionsMap));
      skuForm.setValues(newFormValues);
    } else {
      skuForm.setFieldValue(field.name, newValue, true);
    }
  };

  const handleAddSKU = () => {
    const filters = transformDynamicFormValueToFilterPayload({
      combinations: globalFilterCombinations,
      formFields: globalFilterFormFields,
      formValue: globalFilterValue
    });
    const values = Object.keys(skuForm.values)
      // eslint-disable-next-line array-callback-return, consistent-return
      .map((fieldName) => {
        const fieldValue = skuForm.values[fieldName];
        if (Array.isArray(fieldValue)) {
          const field = formFields.find((f) => f.name === fieldName);
          const options = (fieldValue as ISelectOption[]).map(
            (option) => option.value
          );
          return {
            [fieldName]:
              field.control_type === 'MULTISELECT-DROPDOWN'
                ? options
                : options[0]
          };
        }
        if (fieldValue) {
          return {
            [fieldName]: fieldValue
          };
        }

        // return {
        //   fieldValue ? [fieldName]: fieldValue : ''
        // };
      })
      .reduce<IDynamicFormValue>((prev, curr) => ({ ...prev, ...curr }), {
        is_new_parent: !!skuForm.values.ppgToggle
      });
    setIsAddingSKU(true);
    createSKU(
      sourceId,
      scenarioId,
      innovationModuleId,
      values,
      filters
    ).finally(() => {
      setIsAddingSKU(false);
      onClose(null, null, true);
    });
  };

  useEffect(() => {
    const filters = transformDynamicFormValueToFilterPayload({
      combinations: globalFilterCombinations,
      formFields: globalFilterFormFields,
      formValue: globalFilterValue
    });
    appDispatch(
      getAddSKUFormFields({ moduleId, scenarioId, sourceId, filters })
    ).then((response) => {
      const { fields } = response.payload as { fields: FormField2[] };
      const defaultValues = fields.reduce<IDynamicFormValue>(
        (acc, curr) => ({
          ...acc,
          [curr.name]: curr.default_value
        }),
        {}
      );
      appDispatch(
        toggleDisableStateForSkuFormFields({
          shouldEnableAutoFillFields: false
        })
      );
      const schema = getValidationSchemaForFields(
        getFieldsForValidationSchema(fields, defaultValues.ppgToggle)
      );
      setValidationSchema(Yup.object().shape(schema));
      setInitialValues(defaultValues);
    });
  }, []);

  return (
    <Dialog
      maxWidth='sm'
      open={isOpen}
      onClose={(event, reason) => onClose(event, reason)}
      disableEscapeKeyDown
    >
      <Grid p={2} container justifyContent='space-between'>
        <Grid item xs='auto'>
          <Typography color='primary' fontWeight={700}>
            Add SKU
          </Typography>
        </Grid>
        <Grid item xs='auto'>
          <IconButton
            aria-label='close'
            size='medium'
            color='primary'
            onClick={() => onClose(null, null)}
          >
            <IoCloseCircleOutline />
          </IconButton>
        </Grid>
      </Grid>
      <DialogContent>
        <Grid
          container
          spacing={1}
          sx={{
            border: '1px solid #D0D0D0',
            backgroundColor: '#E7E7E7',
            borderRadius: 0.5,
            p: 2,
            overflowY: 'auto'
          }}
        >
          {isFetchingFormFields ? (
            <LinearProgress />
          ) : (
            formFields
              .filter((field) => {
                if (field.name === 'ppg') {
                  return skuForm.values.ppgToggle
                    ? field.control_type === 'TEXT'
                    : field.control_type === 'SINGLESELECT-DROPDOWN';
                }
                return field.planner_flag;
              })
              // .filter((field) => field.planner_flag)
              .map((field) => (
                <Grid key={field.id} item xs={12} container alignItems='center'>
                  <Grid item xs={4} pr={2} textAlign='right'>
                    {field.name !== 'ppg' ? (
                      <StyledLabel label={field.alias} />
                    ) : null}
                  </Grid>
                  <Grid item xs={8}>
                    <DynamicFormFieldRenderer
                      form={skuForm}
                      handleChange={(value) => handleChange(value, field)}
                      field={field}
                    />
                  </Grid>
                </Grid>
              ))
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <Grid
          container
          justifyContent='flex-end'
          sx={{ p: 2, boxShadow: (theme) => theme.shadows[1] }}
        >
          {isAddingSKU || isFetchingFormFields ? (
            <CircularProgress
              sx={{ height: '20px', width: '20px' }}
              size='small'
            />
          ) : (
            <Button
              sx={{ ml: 1 }}
              disabled={!skuForm.isValid}
              onClick={handleAddSKU}
              variant='contained'
            >
              Add SKU
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
